import { useReducer, useCallback } from 'react';
import {
  StartGameResponse,
  UserDecision,
  DecisionLabel,
  ScoreResult,
} from '../types/index.js';
import { api } from '../api/client.js';

type GamePhase =
  | 'idle'
  | 'loading'
  | 'intro'
  | 'playing'
  | 'submitting'
  | 'results'
  | 'error';

interface GameState {
  phase: GamePhase;
  session: StartGameResponse | null;
  currentIndex: number;
  decisions: UserDecision[];
  cardStartTime: number;
  results: ScoreResult | null;
  error: string | null;
  aiVsHuman: boolean;
}

type Action =
  | { type: 'START_LOADING' }
  | { type: 'SESSION_LOADED'; session: StartGameResponse }
  | { type: 'START_PLAYING' }
  | { type: 'DECIDE'; decision: DecisionLabel }
  | { type: 'SUBMITTING' }
  | { type: 'RESULTS'; results: ScoreResult }
  | { type: 'ERROR'; message: string }
  | { type: 'RESET' }
  | { type: 'TOGGLE_AI_VS_HUMAN' };

const initial: GameState = {
  phase: 'idle',
  session: null,
  currentIndex: 0,
  decisions: [],
  cardStartTime: 0,
  results: null,
  error: null,
  aiVsHuman: true,
};

function reducer(state: GameState, action: Action): GameState {
  switch (action.type) {
    case 'START_LOADING':
      return { ...initial, phase: 'loading', aiVsHuman: state.aiVsHuman };
    case 'SESSION_LOADED':
      return { ...state, phase: 'intro', session: action.session };
    case 'START_PLAYING':
      return { ...state, phase: 'playing', currentIndex: 0, decisions: [], cardStartTime: Date.now() };
    case 'DECIDE': {
      const decision: UserDecision = {
        transactionIndex: state.currentIndex,
        decision: action.decision,
        timeMs: Date.now() - state.cardStartTime,
      };
      const decisions = [...state.decisions, decision];
      const nextIndex = state.currentIndex + 1;
      if (nextIndex >= 10) {
        return { ...state, decisions, phase: 'submitting' };
      }
      return { ...state, decisions, currentIndex: nextIndex, cardStartTime: Date.now() };
    }
    case 'SUBMITTING':
      return { ...state, phase: 'submitting' };
    case 'RESULTS':
      return { ...state, phase: 'results', results: action.results };
    case 'ERROR':
      return { ...state, phase: 'error', error: action.message };
    case 'RESET':
      return { ...initial, aiVsHuman: state.aiVsHuman };
    case 'TOGGLE_AI_VS_HUMAN':
      return { ...state, aiVsHuman: !state.aiVsHuman };
    default:
      return state;
  }
}

export function useGame() {
  const [state, dispatch] = useReducer(reducer, initial);

  const startGame = useCallback(async (storylineId?: string) => {
    dispatch({ type: 'START_LOADING' });
    try {
      const session = await api.startGame(storylineId);
      dispatch({ type: 'SESSION_LOADED', session });
    } catch (err) {
      dispatch({ type: 'ERROR', message: (err as Error).message });
    }
  }, []);

  const beginPlaying = useCallback(() => {
    dispatch({ type: 'START_PLAYING' });
  }, []);

  const decide = useCallback(
    async (decision: DecisionLabel) => {
      dispatch({ type: 'DECIDE', decision });
    },
    [],
  );

  // Called after the last decision triggers 'submitting' phase
  const submitDecisions = useCallback(
    async (decisions: UserDecision[]) => {
      if (!state.session) return;
      try {
        const results = await api.submitDecisions(state.session.sessionId, decisions);
        dispatch({ type: 'RESULTS', results });
      } catch (err) {
        dispatch({ type: 'ERROR', message: (err as Error).message });
      }
    },
    [state.session],
  );

  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);
  const toggleAiVsHuman = useCallback(() => dispatch({ type: 'TOGGLE_AI_VS_HUMAN' }), []);

  return { state, startGame, beginPlaying, decide, submitDecisions, reset, toggleAiVsHuman };
}
